import { getApiUrl, fetchWithTimeout } from '../config/api';

// Type definitions
export interface Notification {
  _id: string;
  title: string;
  content: string;
  createdBy: string | {
    _id: string;
    fullName: string;
    email: string;
  };
  targetType: 'specific' | 'list' | 'role' | 'all';
  targetUsers?: string[];
  targetRole?: 'staff' | 'employer';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  displayType: 'once' | 'dismissible' | 'persistent';
  category?: 'announcement' | 'alert' | 'update' | 'maintenance';
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // User-specific fields
  isRead?: boolean;
  isDismissed?: boolean;
  readAt?: string;
  viewCount?: number;
  // Admin stats
  stats?: {
    totalRecipients: number;
    readCount: number;
    dismissedCount: number;
    activeCount: number;
    readPercentage: number;
    dismissedPercentage: number;
    totalViews: number;
  };
}

export interface CreateNotificationDto {
  title: string;
  content: string;
  targetType: 'specific' | 'list' | 'role' | 'all';
  targetUsers?: string[];
  targetRole?: 'staff' | 'employer';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  displayType: 'once' | 'dismissible' | 'persistent';
  category?: 'announcement' | 'alert' | 'update' | 'maintenance';
  expiresAt?: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  summary?: {
    totalUnread: number;
    totalRead: number;
    urgentUnread: number;
  };
}

export interface UnreadCountResponse {
  count: number;
  urgentCount: number;
  byCategory: {
    [key: string]: number;
  };
}

export interface SendEmailDto {
  targetType: 'specific' | 'list' | 'role' | 'all';
  targetUsers?: string[];
  targetRole?: 'staff' | 'employer';
  subject: string;
  text?: string;
  html?: string;
  from?: string;
  replyTo?: string;
  cc?: string[];
  bcc?: string[];
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Admin API functions
export const notificationService = {
  // Admin: Create notification
  createNotification: async (data: CreateNotificationDto): Promise<Notification> => {
    const response = await fetchWithTimeout(getApiUrl('notifications'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create notification');
    }

    return response.json();
  },

  // Admin: Get all notifications
  getAllNotifications: async (params?: {
    isActive?: boolean;
    targetType?: string;
    targetRole?: string;
    priority?: string;
    category?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<NotificationListResponse> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = getApiUrl(`notifications?${queryParams.toString()}`);
    const response = await fetchWithTimeout(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch notifications');
    }

    return response.json();
  },

  // Admin: Get notification details
  getNotificationById: async (id: string): Promise<Notification> => {
    const response = await fetchWithTimeout(getApiUrl(`notifications/${id}`), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch notification');
    }

    return response.json();
  },

  // Admin: Update notification
  updateNotification: async (
    id: string,
    data: Partial<CreateNotificationDto>
  ): Promise<Notification> => {
    const response = await fetchWithTimeout(getApiUrl(`notifications/${id}`), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update notification');
    }

    return response.json();
  },

  // Admin: Deactivate notification
  deactivateNotification: async (id: string): Promise<{ message: string; notification: Notification }> => {
    const response = await fetchWithTimeout(getApiUrl(`notifications/${id}/deactivate`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to deactivate notification');
    }

    return response.json();
  },

  // Admin: Delete notification
  deleteNotification: async (id: string): Promise<{ message: string }> => {
    const response = await fetchWithTimeout(getApiUrl(`notifications/${id}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete notification');
    }

    return response.json();
  },

  // Admin: Get notification statistics
  getNotificationStats: async (id: string): Promise<any> => {
    const response = await fetchWithTimeout(getApiUrl(`notifications/${id}/stats`), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch notification statistics');
    }

    return response.json();
  },

  // User: Get my notifications
  getMyNotifications: async (params?: {
    status?: 'all' | 'unread' | 'read';
    category?: string;
    priority?: string;
    page?: number;
    limit?: number;
  }): Promise<NotificationListResponse> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    // Add cache buster to prevent stale data
    queryParams.append('_t', Date.now().toString());

    const url = getApiUrl(`notifications/my/notifications?${queryParams.toString()}`);
    const response = await fetchWithTimeout(url, {
      headers: {
        ...getAuthHeaders(),
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch notifications');
    }

    return response.json();
  },

  // User: Get unread count
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    const response = await fetchWithTimeout(getApiUrl('notifications/my/unread-count'), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch unread count');
    }

    return response.json();
  },

  // User: Mark as read
  markAsRead: async (id: string): Promise<any> => {
    const response = await fetchWithTimeout(getApiUrl(`notifications/${id}/read`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to mark notification as read');
    }

    return response.json();
  },

  // User: Dismiss notification
  dismissNotification: async (id: string): Promise<any> => {
    const response = await fetchWithTimeout(getApiUrl(`notifications/${id}/dismiss`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to dismiss notification');
    }

    return response.json();
  },

  // Admin: Send email to notification targets
  sendEmailNotification: async (data: SendEmailDto): Promise<any> => {
    const response = await fetchWithTimeout(getApiUrl('notifications/send-email'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send email notification');
    }

    return response.json();
  },
};
